import {
  Button,
  Div,
  MenuNamesResponse,
  Modal,
  P,
  Tab,
  lightTheme,
  useSafeWindowOpen,
} from '@vgrubs/components';
import { useMemo, useState } from 'react';
import { useLocationBrandPicker } from '../../../store/useLocationBrandPicker';
import { PlaybookSectionCard } from './components/PlaybookSectionCard';
import { PlaybookTabs } from './IntegrationPlaybookModal.styled';
import {
  PlaybookBlock,
  PlaybookLink,
  PlaybookSection,
  PlaybookTabId,
  buildMarketplaceSections,
  buildPosSections,
} from './playbookContent';

/**
 * A brand page has no Marketplace/POS tabs to switch to, so the links that
 * only move between them are dropped rather than left dead.
 */
const withoutTabLinks = (sections: PlaybookSection[]): PlaybookSection[] =>
  sections.map((section) => ({
    ...section,
    blocks: section.blocks.flatMap((block): PlaybookBlock[] => {
      if (block.kind !== 'links') return [block];
      const links = block.links.filter((link) => link.kind !== 'tab');
      return links.length ? [{ ...block, links }] : [];
    }),
  }));

export type ProvidersTab = 'pos' | 'marketplace';

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  locationId: string | number;
  storeName: MenuNamesResponse | null;
  /**
   * A brand has no POS of its own: the POS is connected on the physical
   * location, so a brand only ever reads the marketplace half.
   */
  marketplacesOnly?: boolean;
  /** Sections that talk about this very page switch the tab, not navigate. */
  onSwitchTab: (tab: ProvidersTab) => void;
};

export const IntegrationPlaybookModal = ({
  isOpen,
  closeModal,
  locationId,
  storeName,
  marketplacesOnly,
  onSwitchTab,
}: Props) => {
  const openSafe = useSafeWindowOpen();
  const { setSelectedLocation, setSelectedBrand } = useLocationBrandPicker();

  const [tab, setTab] = useState<PlaybookTabId>(
    marketplacesOnly ? 'marketplaces' : 'pos'
  );

  const posSections = useMemo(() => buildPosSections(locationId), [locationId]);

  const marketplaceSections = useMemo(() => {
    const sections = buildMarketplaceSections();
    return marketplacesOnly ? withoutTabLinks(sections) : sections;
  }, [marketplacesOnly]);

  const sections =
    tab === 'pos' && !marketplacesOnly ? posSections : marketplaceSections;

  const openHref = (href: string) =>
    openSafe(href, { immediate: true, target: '_blank' });

  const handleOpenLink = (link: PlaybookLink) => {
    switch (link.kind) {
      case 'tab':
        onSwitchTab(link.tab);
        closeModal();
        return;
      case 'url':
        openHref(link.href);
        return;
      case 'workingHours':
        // The Working Hours page reads its location from the persisted picker.
        setSelectedLocation(storeName?.location ?? null);
        setSelectedBrand(null);
        openHref('/working-hours');
        return;
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={closeModal}
      logo={false}
      header="Integration playbook"
      subtitle={storeName?.locationName}
      width="760px"
      textAlign="left"
    >
      <P $mb="16" $color={lightTheme.lightGray}>
        The rules and the timings behind a POS and its marketplaces. Read the
        part you need; links open in a new tab so this stays open.
      </P>

      {!marketplacesOnly && (
        <PlaybookTabs>
          <Tab
            $small
            $minWidthAuto
            selected={tab === 'pos'}
            onClick={() => setTab('pos')}
          >
            POS
          </Tab>
          <Tab
            $small
            $minWidthAuto
            selected={tab === 'marketplaces'}
            onClick={() => setTab('marketplaces')}
          >
            Marketplaces
          </Tab>
        </PlaybookTabs>
      )}

      <Div $flex $column $gap="16px">
        {sections.map((section) => (
          <PlaybookSectionCard
            key={section.id}
            section={section}
            onOpenLink={handleOpenLink}
          />
        ))}
      </Div>

      <Div $flex $justifyEnd $mt="24">
        <Button $selected $w="140px" onClick={closeModal}>
          Close
        </Button>
      </Div>
    </Modal>
  );
};
